import React from 'react';
import moment from 'moment';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  AccessTime,
  Campaign,
  Close,
  DevicesOther,
  Person,
  Public,
  Timeline,
  TouchApp,
  Visibility,
} from '@mui/icons-material';

const UserJourneyStats = ({ sessionData, sessionId, onClose }) => {
  if (!sessionData || sessionData.length === 0) {
    return (
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">No Data Available</Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>No journey data available for this session.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Calculate session statistics
  const firstAction = sessionData[0];
  const lastAction = sessionData[sessionData.length - 1];
  const sessionStart = moment(firstAction.TimeStampDate);
  const sessionEnd = moment(lastAction.TimeStampDate);
  const sessionDuration = moment.duration(sessionEnd.diff(sessionStart));
  
  const pageViews = sessionData.filter(action => action.UserActionType === 'page_view');
  const uniquePages = [...new Set(pageViews.map(action => action.Href))];
  
  const conversionCount = sessionData.reduce((sum, action) => sum + (action.ConversionCount || 0), 0);
  const isConverted = conversionCount > 0;
  
  // Group actions by type
  const actionTypes = {};
  sessionData.forEach(action => {
    if (!actionTypes[action.UserActionType]) {
      actionTypes[action.UserActionType] = 0;
    }
    actionTypes[action.UserActionType]++;
  });

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Session Statistics
            <Chip 
              size="small" 
              label={`${sessionData.length} actions`} 
              sx={{ ml: 2 }} 
              color="primary" 
              variant="outlined"
            />
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="subtitle2" color="textSecondary">
          Session ID: {sessionId}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, borderBottom: '1px solid #eee', pb: 1 }}>
                Session Overview
              </Typography>
              <List dense sx={{ '& .MuiListItem-root': { py: 1.5 } }}>
                <ListItem>
                  <ListItemIcon>
                    <AccessTime fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Session Duration" 
                    secondary={`${Math.floor(sessionDuration.asMinutes())} minutes ${Math.floor(sessionDuration.seconds())} seconds`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Timeline fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Total Actions" 
                    secondary={sessionData.length} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Visibility fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Page Views" 
                    secondary={pageViews.length} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Public fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Unique Pages" 
                    secondary={uniquePages.length} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TouchApp fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Conversions" 
                    secondary={
                      <Chip 
                        size="small" 
                        label={conversionCount} 
                        color={isConverted ? "success" : "default"} 
                      />
                    } 
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, borderBottom: '1px solid #eee', pb: 1 }}>
                User Information
              </Typography>
              <List dense sx={{ '& .MuiListItem-root': { py: 1.5 } }}>
                <ListItem>
                  <ListItemIcon>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="User Type" 
                    secondary={
                      <Chip 
                        size="small" 
                        label={lastAction.NextUserType || 'Unknown'} 
                        color={
                          lastAction.NextUserType === 'high' ? 'success' : 
                          lastAction.NextUserType === 'medium' ? 'warning' : 'default'
                        } 
                      />
                    } 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DevicesOther fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Browser Type" 
                    secondary={firstAction.UserBrowserType || 'Unknown'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Campaign fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Source / Medium" 
                    secondary={`${firstAction.SessionFcSource || 'Direct'} / ${firstAction.SessionFcMedium || 'None'}`} 
                  />
                </ListItem>
                {firstAction.SessionFcCampaign && (
                  <ListItem>
                    <ListItemIcon>
                      <Campaign fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Campaign" 
                      secondary={firstAction.SessionFcCampaign} 
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
          
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, borderBottom: '1px solid #eee', pb: 1 }}>
                Session Timeline
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto', pr: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Start Time
                    </Typography>
                    <Typography variant="body2">
                      {sessionStart.format('MMM DD, YYYY HH:mm:ss')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" align="right">
                      End Time
                    </Typography>
                    <Typography variant="body2" align="right">
                      {sessionEnd.format('MMM DD, YYYY HH:mm:ss')}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>
                  Page Visit Sequence
                </Typography>
                <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {pageViews.slice().reverse().map((action, index) => {
                    const url = action.Href ? new URL(action.Href) : null;
                    return (
                      <ListItem key={index} divider={index < pageViews.length - 1}>
                        <ListItemIcon>
                          <Chip size="small" label={index + 1} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={url ? url.pathname : 'Unknown Page'} 
                          secondary={moment(action.TimeStampDate).format('HH:mm:ss')} 
                          primaryTypographyProps={{ 
                            style: { 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap' 
                            } 
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserJourneyStats;